import React from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import PhoneInput from "../../../../shared/PhoneInput";
import NameInput from "../../../../shared/NameInput";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useTheme } from "../../../../../contexts/ThemeContext";
import { 
  INPUT_BASE, 
  SELECT_BASE, 
  LABEL_BASE,
  CARD_SPACING 
} from "../../../../../constants/styles";

export default function MemberEditModal({
  isOpen,
  onClose,
  data,
  onFieldChange,
  errors = {},
  prefixes = [],
  suffixes = [],
  genderTypes = [],
  pipelineData = { stages: [], currentStage: null },
}) {
  const { isDarkMode } = useTheme();
  const [showSSN, setShowSSN] = React.useState(false);
  const [selectedStage, setSelectedStage] = React.useState(
    pipelineData.currentStage?.stage_id?.toString() || ""
  );
  const [selectedStatus, setSelectedStatus] = React.useState(
    pipelineData.currentStage?.status_id?.toString() || ""
  );

  if (!isOpen) return null;

  const handleSave = () => {
    if (
      selectedStage !== pipelineData.currentStage?.stage_id?.toString()
    ) {
      onFieldChange("pipelineStage", Number(selectedStage));
    }
    if (
      selectedStatus !== pipelineData.currentStage?.status_id?.toString()
    ) {
      onFieldChange("pipelineStatus", Number(selectedStatus));
    }
    onClose();
  };

  // Get current stage statuses
  const getCurrentStageStatuses = () => {
    if (!selectedStage) return [];
    const stage = pipelineData.stages.find(
      (s) => s.stage_id === Number(selectedStage)
    );
    return stage?.statuses || [];
  };

  const inputBaseClasses = `${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400`;
  const selectBaseClasses = `${SELECT_BASE} dark:bg-gray-700 dark:border-gray-600`;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative z-[10000]">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Edit Member Information
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Name row */}
          <div className={`flex ${CARD_SPACING.grid}`}>
            {/* Prefix */}
            <div className="w-20">
              <label className={LABEL_BASE}>Prefix</label>
              <select
                className={selectBaseClasses}
                value={data.prefix || ""}
                onChange={(e) => onFieldChange("prefix", e.target.value)}
              >
                <option value="">—</option>
                {prefixes.map((p) => (
                  <option key={p.name_prefix} value={p.name_prefix}>
                    {p.name_prefix}
                  </option>
                ))}
              </select>
            </div>
            {/* First */}
            <div className="flex-[1.5]">
              <label className={LABEL_BASE}>First Name *</label>
              <NameInput
                name="firstName"
                className={inputBaseClasses}
                value={data.firstName || ""}
                onChange={(e) => onFieldChange("firstName", e.target.value)}
                nickname={data.preferredName}
                onNicknameChange={(nick) =>
                  onFieldChange("preferredName", nick)
                }
              />
            </div>
            {/* Middle */}
            <div className="flex-1">
              <label className={LABEL_BASE}>Middle Name</label>
              <input
                type="text"
                className={inputBaseClasses}
                value={data.middleName || ""}
                onChange={(e) => onFieldChange("middleName", e.target.value)}
              />
            </div>
            {/* Last */}
            <div className="flex-[1.5]">
              <label className={LABEL_BASE}>Last Name *</label>
              <NameInput
                name="lastName"
                className={inputBaseClasses}
                value={data.lastName || ""}
                onChange={(e) => onFieldChange("lastName", e.target.value)}
                required
              />
            </div>
            {/* Suffix */}
            <div className="w-20">
              <label className={LABEL_BASE}>Suffix</label>
              <select
                className={selectBaseClasses}
                value={data.suffix || ""}
                onChange={(e) => onFieldChange("suffix", e.target.value)}
              >
                <option value="">—</option>
                {suffixes.map((s) => (
                  <option key={s.name_suffix} value={s.name_suffix}>
                    {s.name_suffix}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* DOB & Gender & SSN */}
          <div className={`flex ${CARD_SPACING.grid}`}>
            {/* Gender */}
            <div className="w-40">
              <label className={LABEL_BASE}>Gender</label>
              <select
                className={selectBaseClasses}
                value={data.genderTypeId || ""}
                onChange={(e) =>
                  onFieldChange("genderTypeId", Number(e.target.value))
                }
              >
                <option value="">—</option>
                {genderTypes.map((g) => (
                  <option key={g.gender_type_id} value={g.gender_type_id}>
                    {g.gender_type_name}
                  </option>
                ))}
              </select>
            </div>
            {/* SSN */}
            <div className="flex-1">
              <label className={LABEL_BASE}>SSN</label>
              <div className="relative">
                <input
                  type={showSSN ? "text" : "password"}
                  className={`${inputBaseClasses} ${errors.ssn ? "border-red-500" : ""}`}
                  value={data.ssn || ""}
                  onChange={(e) => onFieldChange("ssn", e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowSSN((v) => !v)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-gray-400"
                >
                  {showSSN ? (
                    <FiEyeOff className="h-4 w-4" />
                  ) : (
                    <FiEye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            {/* Date of Birth */}
            <div className="flex-1">
              <label className={LABEL_BASE}>Date of Birth *</label>
              <input
                type="date"
                className={inputBaseClasses}
                value={data.dateOfBirth || ""}
                onChange={(e) =>
                  onFieldChange("dateOfBirth", e.target.value)
                }
                required
              />
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Contact Information
            </h4>
            <div className={CARD_SPACING.section}>
              {/* Emails */}
              <div className={`grid grid-cols-2 ${CARD_SPACING.grid}`}>
                <div>
                  <label className={LABEL_BASE}>Email *</label>
                  <input
                    type="email"
                    className={`${inputBaseClasses} ${errors.email ? "border-red-500" : ""}`}
                    value={data.email || ""}
                    onChange={(e) => onFieldChange("email", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className={LABEL_BASE}>Alternate Email</label>
                  <input
                    type="email"
                    className={inputBaseClasses}
                    value={data.alternateEmail || ""}
                    onChange={(e) =>
                      onFieldChange("alternateEmail", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Phones */}
              <div className={`grid grid-cols-2 ${CARD_SPACING.grid}`}>
                <div>
                  <label className={LABEL_BASE}>Phone Number *</label>
                  <PhoneInput
                    className={inputBaseClasses}
                    value={data.phoneNumber}
                    onChange={(val) => onFieldChange("phoneNumber", val)}
                    type={data.phoneType}
                    onTypeChange={(t) => onFieldChange("phoneType", t)}
                    error={errors.phoneNumber}
                  />
                </div>
                <div>
                  <label className={LABEL_BASE}>Alternate Phone</label>
                  <PhoneInput
                    className={inputBaseClasses}
                    value={data.alternatePhone}
                    onChange={(val) => onFieldChange("alternatePhone", val)}
                    type={data.alternatePhoneType}
                    onTypeChange={(t) =>
                      onFieldChange("alternatePhoneType", t)
                    }
                    error={errors.alternatePhone}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Pipeline Status */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Relationship Pipeline
            </h4>
            <div className={`grid grid-cols-2 ${CARD_SPACING.grid}`}>
              {/* Stage */}
              <div>
                <label className={LABEL_BASE}>Stage</label>
                <select
                  className={selectBaseClasses}
                  value={selectedStage}
                  onChange={(e) => {
                    setSelectedStage(e.target.value);
                    setSelectedStatus(""); // reset status
                  }}
                >
                  <option value="">Select Stage</option>
                  {pipelineData.stages.map((s) => (
                    <option key={s.stage_id} value={s.stage_id}>
                      {s.stage_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div>
                <label className={LABEL_BASE}>Status</label>
                <select
                  className={selectBaseClasses}
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value="">Select Status</option>
                  {getCurrentStageStatuses().map((st) => (
                    <option key={st.status_id} value={st.status_id}>
                      {st.status_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
} 